import { FC, CSSProperties, useMemo, useEffect, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { useMutation, useQuery } from 'react-query';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { useStore } from 'hooks/useRootStore';
import { useS3ImageUploader } from 'hooks/useS3ImageUploader';
import { makeSelectOptions } from 'utils/makeSelectOptions';
import { TextAreaInput } from 'components/Inputs/TextAreaInput';
import SelectComponent from 'components/Inputs/SelectComponent';
import { ImageUploader } from 'components/Core/ImageUploader/ImageUploader';
import { DatePickerComponent } from 'components/Inputs/DatePickerComponents/DatePickerComponent';
import { IOption } from 'components/Inputs/SelectComponent/types';
import { Button } from 'components/Core/Button';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import { DiscountType, IProductData } from 'stores/products/types';
import { getCurrencyCodeFromCountry } from 'utils/getCurrencySymbol';
import { ProductBasicFormData, ProductFormTransFields } from '../ProductBasicForm/types';
import { productFormTranslations } from '../ProductBasicForm/translations';
import { discountTypeOptions, PRICE_DEFAULT_MIN } from '../ProductBasicForm/constants';
import styles from './productForm.module.scss';

const buttonStyleProp: CSSProperties = {
  width: '100%',
};

export const ProductCatalogueForm: FC = observer(() => {
  const {
    brandsStore: { brandBasicData, brandsCatalogue },
    productsStore: { productsCategories, getProductsCategories, addProduct, selectedProduct, setSelectedProduct },
    mainStore: { user },
    alertStore: { showAlert },
    popUpStore: { callBack, closePopUp },
  } = useStore();

  const { t } = useMyTranslations<ProductFormTransFields>([productFormTranslations.name]);

  useEffect(() => {
    return () => setSelectedProduct(null);
  }, []);

  useQuery('getProductsCategories', getProductsCategories, {
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const { mutate: addProductMutation, isLoading: isLoadingAdding } = useMutation(addProduct, {
    mutationKey: 'addPoint',
    onSuccess: (res: IProductData) => {
      showAlert(`create point success`, 'success');
      callBack && callBack.cb(res);
      closePopUp();
    },
  });

  const defaultValues = useMemo((): Partial<ProductBasicFormData> | undefined => {
    let def: Partial<ProductBasicFormData> = selectedProduct || {};
    if (!selectedProduct) {
      if (user)
        def = {
          ...def,
          currencyCode: getCurrencyCodeFromCountry(user.selectedCountryCode.countryCode),
        };
      if (brandsCatalogue) {
        const today = new Date();
        const { startDate, expiredDate } = brandsCatalogue;
        const status = new Date(startDate) <= today && new Date(expiredDate) >= today ? 'Active' : 'Inactive';
        def = { ...def, status };
      }
    }
    if (brandBasicData) {
      def = { ...def, brand: brandBasicData };
    }
    return def;
  }, [brandBasicData, brandsCatalogue, user]);

  const useFormMethods = useForm<ProductBasicFormData>({ defaultValues });

  const {
    handleSubmit,
    watch,
    getValues,
    setValue,
    reset,
    trigger,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { errors, isSubmitting }, // should create errors conts to update errors when they appears
  } = useFormMethods;

  /* MOB1-2259: as in ProductBasicForm — a country switch while a NEW product is being filled in starts the
     form over with the new country's currency; an existing product keeps its own */
  useEffect(() => {
    if (selectedProduct) return;
    reset(defaultValues);
  }, [user?.selectedCountryCode.countryCode]);

  const formatNumber = useCallback((num: number) => {
    return +num.toFixed(2);
  }, []);

  const onPriceBlur = useCallback((val: number) => {
    if (getValues('discountType')) {
      // change discount due to changed price
      const d = getValues('discountValue');
      if (val !== undefined && val > 0 && !!d) {
        if (getValues('discountType') === 'fixed') {
          setValue('discountPrice', formatNumber(val - d));
        } else {
          setValue('discountPrice', formatNumber(Math.round(val * (1 - d / 100) * 100) / 100) || PRICE_DEFAULT_MIN);
        }
      }
      trigger('discountPrice');
      trigger('discountValue');
    }
  }, []);

  const onDiscountTypeSelect = useCallback((val: IOption | null) => {
    setValue('discountType', val?.value as DiscountType | undefined);
    if (val) {
      // change discount relative to type
      const p = getValues('price');
      const d = getValues('discountValue');
      if (p !== undefined && d !== undefined && d !== null) {
        if (val.value === 'fixed') {
          setValue('discountPrice', formatNumber(p - d));
        } else {
          setValue('discountPrice', formatNumber(Math.round(p * (1 - d / 100) * 100) / 100) || PRICE_DEFAULT_MIN);
        }
        trigger('discountValue');
        trigger('discountPrice');
      }
    } else {
      // erase discount
      setValue('discountValue', undefined);
      setValue('discountPrice', undefined);
      setValue('startDate', undefined);
      setValue('discountValue', undefined);
    }
  }, []);

  const onDiscountValueBlur = useCallback((val: number) => {
    // change discountPrice due to changed discount value
    const p = getValues('price');
    if (p !== undefined && p > 0 && val !== undefined && val !== null) {
      if (getValues('discountType') === 'fixed') {
        setValue('discountPrice', formatNumber(p - val));
      } else {
        setValue('discountPrice', formatNumber(Math.round(p * (1 - val / 100) * 100) / 100) || PRICE_DEFAULT_MIN);
      }
    }
    trigger('discountValue');
    trigger('discountPrice');
  }, []);

  const onDiscountPriceBlur = useCallback((val: number) => {
    // change discountPrice due to changed discount price
    const p = getValues('price');
    if (p !== undefined && val !== undefined && val !== null) {
      if (getValues('discountType') === 'fixed') {
        setValue('discountValue', formatNumber(p - val));
      } else {
        setValue('discountValue', formatNumber(Math.round(100 * (1 - val / p) * 100) / 100) || PRICE_DEFAULT_MIN);
      }
    }
    trigger('discountValue');
    trigger('discountPrice');
  }, []);

  const { uploadImage, isLoading: isImageUploading } = useS3ImageUploader<ProductBasicFormData>({ setValue });

  const onSubmitHandler = useCallback(
    async (formData: ProductBasicFormData) => {
      const { imageUrlFile: iFile } = formData;
      try {
        const path = `${user?.selectedCountryCode.countryCode.toLowerCase()}/${brandBasicData?.slug}/admin-products`;
        if (iFile) await uploadImage(iFile, 'imageUrl', path);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { imageUrlFile, ...data } = getValues();
        const newData = { ...(selectedProduct || {}), ...data };
        addProductMutation(newData);
      } catch {
        showAlert('Problems with image upload', 'warning');
      }
    },
    [brandBasicData, user, selectedProduct]
  );

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormProvider {...(useFormMethods as any)}>
      <div className={styles.content}>
        <form className={styles.form}>
          <section className={`${styles.formSection}`}>
            <h6 className={`${styles.title} ${styles.titleWithMargin}`}>{t('title')}</h6>
            <ControlledInput name='name' type='text' label={t('name')} required />
            <TextAreaInput name='description' label={t('description')} placeholder={t('description')} />
            <ControlledInput
              name='price'
              type='money'
              label={t('price')}
              icon={<p className={styles.currency}>{getValues('currencyCode')}</p>}
              iconPosition='right'
              required
              min={PRICE_DEFAULT_MIN}
              myOnBlur={(val) => onPriceBlur(+val)}
            />
            <SelectComponent
              name='categories'
              label={t('categories')}
              placeholder=''
              options={productsCategories || []}
              optionLabel='categoryName'
              optionValue='id'
              maxMenuHeight={160}
              isMulti
              required
            />
            <SelectComponent
              name='discountType'
              label={t('discountType')}
              placeholder=''
              options={makeSelectOptions(discountTypeOptions)}
              onSelect={(val) => onDiscountTypeSelect(val as unknown as IOption)}
              isDisabled={!watch('price')}
            />
            <ControlledInput
              name='discountValue'
              type='money'
              label={t('discountValue')}
              icon={
                watch('discountType') ? (
                  <p className={styles.currency}>
                    {watch('discountType') === 'fixed' ? getValues('currencyCode') : '%'}
                  </p>
                ) : undefined
              }
              iconPosition='right'
              disabled={!watch('discountType')}
              required={!!watch('discountType')}
              min={PRICE_DEFAULT_MIN}
              max={(watch('discountType') === 'fixed' ? watch('price') : 100) - PRICE_DEFAULT_MIN}
              myOnBlur={(val) => onDiscountValueBlur(+val)}
            />
            <ControlledInput
              name='discountPrice'
              type='money'
              label={t('discountPrice')}
              placeholder=''
              icon={watch('discountType') ? <p className={styles.currency}>{getValues('currencyCode')}</p> : undefined}
              iconPosition='right'
              disabled={!watch('discountType')}
              required={!!watch('discountType')}
              min={PRICE_DEFAULT_MIN}
              max={watch('price') - PRICE_DEFAULT_MIN}
              myOnBlur={(val) => onDiscountPriceBlur(+val)}
            />
            <DatePickerComponent
              label={t('startDate')}
              name='startDate'
              buttonStyleProp={buttonStyleProp}
              stylesProp={{ marginBottom: 24 }}
              maxDate={watch('expiredDate') ? new Date(watch('expiredDate') || '') : undefined}
              noMaxDate={!watch('expiredDate')}
              disabled={!watch('discountType')}
              required={!!watch('discountType')}
            />
            <DatePickerComponent
              label={t('expiredDate')}
              name='expiredDate'
              buttonStyleProp={buttonStyleProp}
              stylesProp={{ marginBottom: 24 }}
              noMaxDate
              minDate={watch('startDate') ? new Date(watch('startDate') || '') : undefined}
              disabled={!watch('startDate')}
              required={!!watch('discountType')}
            />
          </section>
          <section className={`${styles.rightSideSection}`}>
            <ImageUploader name='imageUrl' title={t('imageUrl')} path='products' required />
          </section>
        </form>
        <div className={styles.bottomSection}>
          <Button
            title={'Save'}
            onClick={handleSubmit(onSubmitHandler)}
            isDisabled={isImageUploading || isLoadingAdding || isSubmitting}
          />
        </div>
      </div>
    </FormProvider>
  );
});
